// src/pages/OrderStep3.js
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import PaymentGateway from '../components/payment/PaymentGateway';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';

export default function OrderStep3() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    const data = localStorage.getItem('order_summary');
    if (!data) {
      navigate('/order-step-1');
    } else {
      setSummary(JSON.parse(data));
    }
  }, [navigate]);

  const handlePaymentSuccess = async (paymentData) => {
    // Order was already placed in step 2, payment is now captured
    localStorage.removeItem('order_summary');
    localStorage.removeItem('cart_meds');
    navigate(`/order-success?order_id=${summary.orderStringId || summary.orderId}&total=${summary.total}&payment_id=${paymentData.razorpay_payment_id || paymentData.payment_id || 'PAID'}&service_type=medicine`);
  };
  
  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    setPaymentError('Payment failed. Please try again.');
    setLoading(false);
  };
  
  const initiatePayment = () => {
    if (!token) {
      alert('Please login to complete payment');
      navigate('/login');
      return;
    }
    setShowPayment(true);
    setPaymentError('');
  };

  if (!summary) return (
    <>
      <Header />
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading order info...</p>
      </div>
      <Footer />
    </>
  );

  const totalQty = summary.medicines.reduce((sum, m) => sum + m.qty, 0);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Order Summary & Payment</h2>
            
            {/* Order Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Details</h3>
              <div className="space-y-2 text-sm md:text-base">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Items:</span>
                  <span className="font-medium">{totalQty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{(summary.total - summary.deliveryFee).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery Fee:</span>
                  <span className="font-medium">₹{summary.deliveryFee}</span>
                </div>
                <div className="border-t pt-2 flex justify-between text-lg font-bold" style={{ color: '#104a5f' }}>
                  <span>Total Amount:</span>
                  <span>₹{summary.total}</span>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {paymentError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {paymentError}
              </div>
            )}

            {/* Payment Section */}
            {!showPayment ? (
              <div className="text-center">
                <p className="text-gray-600 mb-6">Click below to proceed with secure payment</p>
                <button
                  onClick={initiatePayment}
                  className="px-8 py-3 text-white font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:scale-105"
                  style={{ backgroundColor: '#104a5f' }}
                >
                  Proceed to Payment
                </button>
              </div>
            ) : (
              <PaymentGateway
                amount={summary.total}
                serviceType="medicine"
                serviceId={summary.orderId}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                customerDetails={{
                  name: user?.name || '',
                  email: user?.email || '',
                  mobile: user?.mobile || ''
                }}
              />
            )}

            {loading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: '#104a5f' }}></div>
                  <p className="text-gray-600">Processing your order...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
